// Vanilla PKCE flow against Cognito hosted UI.
//
// The Cognito User Pool Client is configured for AllowedOAuthFlows=["code"]
// (Authorization Code with PKCE), so the SPA must:
//   1. generate code_verifier + code_challenge
//   2. redirect to Cognito hosted UI /oauth2/authorize?response_type=code&...
//   3. on /auth/callback?code=..., POST /oauth2/token with the code + verifier
//   4. store id_token + refresh_token in sessionStorage
//   5. include Authorization: Bearer ${id_token} on /api/* fetches
//   6. on token expiry, exchange refresh_token for a fresh id_token
//
// No Cognito SDK is pulled in -- ~120 lines of vanilla code is preferable to
// a 200KB amazon-cognito-identity-js / aws-amplify dependency.

const CFG = {
  domain:
    (import.meta.env.VITE_COGNITO_DOMAIN as string | undefined) ??
    "https://trumin-prod-coding-dashboard.auth.eu-west-2.amazoncognito.com",
  clientId:
    (import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined) ??
    "479rbperl33tcr01t78f10cldl",
  redirectUri:
    (import.meta.env.VITE_COGNITO_REDIRECT_URI as string | undefined) ??
    `${window.location.origin}/auth/callback`,
  identityProvider:
    (import.meta.env.VITE_COGNITO_IDP as string | undefined) ??
    "IdentityCenter",
  scopes: "email openid profile",
};

const STORAGE_KEYS = {
  idToken: "cognito.idToken",
  refreshToken: "cognito.refreshToken",
  idTokenExpiry: "cognito.idTokenExpiry",
  pkceVerifier: "cognito.pkceVerifier",
} as const;

// ---------------------------------------------------------------------------
// PKCE primitives
// ---------------------------------------------------------------------------

function base64UrlEncode(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function sha256(input: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return new Uint8Array(digest);
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function challenge(verifier: string): Promise<string> {
  return base64UrlEncode(await sha256(verifier));
}

// ---------------------------------------------------------------------------
// Storage helpers (sessionStorage; cleared on browser close)
// ---------------------------------------------------------------------------

function setIdTokenWithExpiry(idToken: string, expiresInSec: number): void {
  sessionStorage.setItem(STORAGE_KEYS.idToken, idToken);
  sessionStorage.setItem(
    STORAGE_KEYS.idTokenExpiry,
    String(Date.now() + expiresInSec * 1000),
  );
}

function getStoredIdToken(): string | null {
  const token = sessionStorage.getItem(STORAGE_KEYS.idToken);
  const expiry = Number(sessionStorage.getItem(STORAGE_KEYS.idTokenExpiry) ?? 0);
  if (!token || !expiry) return null;
  // Treat as expired if less than 60s of validity remain.
  if (Date.now() > expiry - 60_000) return null;
  return token;
}

function getRefreshToken(): string | null {
  return sessionStorage.getItem(STORAGE_KEYS.refreshToken);
}

function clearTokens(): void {
  sessionStorage.removeItem(STORAGE_KEYS.idToken);
  sessionStorage.removeItem(STORAGE_KEYS.refreshToken);
  sessionStorage.removeItem(STORAGE_KEYS.idTokenExpiry);
  sessionStorage.removeItem(STORAGE_KEYS.pkceVerifier);
}

// ---------------------------------------------------------------------------
// OAuth2 wire calls
// ---------------------------------------------------------------------------

async function exchangeCodeForTokens(code: string, verifier: string): Promise<void> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: CFG.clientId,
    code,
    redirect_uri: CFG.redirectUri,
    code_verifier: verifier,
  });
  const r = await fetch(`${CFG.domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!r.ok) {
    throw new Error(`Cognito token exchange failed: ${r.status} ${await r.text()}`);
  }
  const j = await r.json();
  // APIGW v2 + Cognito User Pool integration expects access_token, not
  // id_token, on the Authorization: Bearer header. Cognito's /oauth2/token
  // returns both; we store the access_token for /api/* fetches. The id_token
  // is still in the response if SPA-side identity display ever needs it
  // (decodeIdTokenClaims still reads from the same storage key for the
  // diagnostic Debug button -- it now decodes the access token's claims,
  // which carry iss/client_id/sub/exp; for cognito-username/email use a
  // separate id_token store later if needed).
  setIdTokenWithExpiry(j.access_token, j.expires_in);
  if (j.refresh_token) {
    sessionStorage.setItem(STORAGE_KEYS.refreshToken, j.refresh_token);
  }
}

async function refreshIdToken(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: CFG.clientId,
    refresh_token: refresh,
  });
  const r = await fetch(`${CFG.domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!r.ok) return null;
  const j = await r.json();
  setIdTokenWithExpiry(j.access_token, j.expires_in);
  return j.access_token;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function signIn(): Promise<void> {
  const verifier = randomVerifier();
  sessionStorage.setItem(STORAGE_KEYS.pkceVerifier, verifier);
  // Diagnostic: log a short prefix of the verifier (NOT the full value) so
  // Duncan can correlate sign-in vs callback console entries without
  // exposing the verifier.
  console.warn("[cognito.signIn] verifier written, prefix:", verifier.slice(0, 8));
  const ch = await challenge(verifier);
  const params = new URLSearchParams({
    response_type: "code",
    client_id: CFG.clientId,
    redirect_uri: CFG.redirectUri,
    scope: CFG.scopes,
    code_challenge: ch,
    code_challenge_method: "S256",
    identity_provider: CFG.identityProvider,
  });
  window.location.assign(`${CFG.domain}/oauth2/authorize?${params.toString()}`);
}

export class PkceVerifierMissingError extends Error {
  constructor() {
    super(
      "PKCE verifier missing from sessionStorage on callback. The verifier was either never written (callback opened directly without going through sign-in) or was cleared between sign-in and callback (browser privacy setting, sessionStorage limit, different tab/window, or browser restart). sessionStorage is per-tab; opening the Cognito flow in one tab and the callback in another loses the verifier.",
    );
    this.name = "PkceVerifierMissingError";
  }
}

export class CallbackCodeMissingError extends Error {
  constructor() {
    super(
      "Cognito callback URL missing the `code` parameter. Cognito did not return an authorisation code — likely a Cognito-side configuration or hosted-UI failure rather than a client issue.",
    );
    this.name = "CallbackCodeMissingError";
  }
}

export async function handleCallback(): Promise<void> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  const verifier = sessionStorage.getItem(STORAGE_KEYS.pkceVerifier);
  // Diagnostic: log presence (not value) so Duncan can capture which side
  // failed by inspecting devtools console without exposing the verifier.
  console.warn("[cognito.handleCallback] code present:", !!code, "verifier present:", !!verifier);
  if (!code) throw new CallbackCodeMissingError();
  if (!verifier) throw new PkceVerifierMissingError();
  await exchangeCodeForTokens(code, verifier);
  sessionStorage.removeItem(STORAGE_KEYS.pkceVerifier);
  // Hard-navigate to / so React re-renders the Dashboard. replaceState
  // alone updates the URL bar but does not trigger any React-observable
  // state change, leaving CallbackHandler mounted forever. Tokens persist
  // in sessionStorage across the reload.
  window.location.replace("/");
}

export async function getIdToken(): Promise<string | null> {
  const stored = getStoredIdToken();
  if (stored) return stored;
  return refreshIdToken();
}

export function signOut(): void {
  clearTokens();
  const params = new URLSearchParams({
    client_id: CFG.clientId,
    logout_uri: `${window.location.origin}/`,
  });
  window.location.assign(`${CFG.domain}/logout?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Diagnostic helpers (no signature verification)
// ---------------------------------------------------------------------------

/** Decode the ID token claims for diagnostic display. NOT a verification --
 *  trust boundary is server-side only. Used by ErrorBanner debug to compare
 *  what the SPA holds against what APIGW JWT authoriser expects (iss / aud /
 *  token_use / sub / exp). */
export function decodeIdTokenClaims(): Record<string, unknown> | null {
  const token = sessionStorage.getItem(STORAGE_KEYS.idToken);
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload + "===".slice((payload.length + 3) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}
